import * as React from 'react';
import { useState } from 'react';
import { Input } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import OWButton from '../../../components/ow_button/OWButton';
import { withUserType } from '../../../components/withUserType';
import { cn } from '../../../utils/ClassNamesUtils';
import { AgentPageContext } from '../../../types/agentHarness';
import { getOscarTheme } from '../../constants/AIAgentTheme';
import { OscarRoleProvider } from '../../context/OscarRoleContext';
import { useOscarContextEnabled } from '../../hooks/useAgentChatEnabled';
import { callOscar } from '../../utils/OscarInvocation';
import { OscarMark } from '../shared/OscarMark';

const EASE = 'ease-[cubic-bezier(.645,.045,.355,1)]';

interface OscarAskInputProps {
	placeholder: string;
	/**
	 * The record(s) the question is about. Omit it on a page Oscar already recognises from the
	 * route (`oscarPageContextFor`) and the dock sends that.
	 */
	context?: AgentPageContext | null;
	/** Chips under the input, e.g. suggested questions. */
	children?: React.ReactNode;
	/**
	 * Placement, including outer spacing: the box has no margin of its own (e.g. `mb-m` above a
	 * right column's cards). Custom spacing tokens do not merge in `cn`, so a default margin here
	 * could not be overridden.
	 */
	className?: string;
	/** Injected by `withUserType`. */
	userType?: string;
}

/**
 * "Ask me anything" for a record page. Submitting clears the field and hands the question to
 * Oscar with `callOscar`, which opens the drawer.
 *
 * The input row shows only with `agent-chat` and `oscar-context` on and the user in Oscar's audience
 * (a buyer, or a buyer's internal team). The chips row renders either way, so a surface's own chips keep
 * working without Oscar; with nothing to show, the box collapses (`empty:hidden`).
 */
const OscarAskInput = ({
	placeholder,
	context,
	children,
	className,
	userType,
}: OscarAskInputProps) => {
	// `agent-chat` + `oscar-context`, for Oscar's audience; subscribed, since flags can land after render.
	const showInput = useOscarContextEnabled(userType);
	const [question, setQuestion] = useState('');
	const [focused, setFocused] = useState(false);

	const theme = getOscarTheme(userType);
	const trimmed = question.trim();
	// The owl steps aside while the field is in use; the send button takes its place on the right.
	const active = focused || !!question;

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		setQuestion(event.target.value);
	const handleFocus = () => setFocused(true);
	const handleBlur = () => setFocused(false);

	const send = () => {
		if (!trimmed) return;
		setQuestion('');
		callOscar({ prompt: trimmed, context });
	};

	const handlePressEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
		send();
		event.currentTarget.blur();
	};

	// Keeps the field focused when the send button is pressed, so it does not jump mid-click.
	const keepFocus = (event: React.MouseEvent) => event.preventDefault();

	return (
		<OscarRoleProvider userType={userType}>
			<div
				className={cn(
					// Without the input row the chips sit directly in the box, so when none of them
					// renders the box is `:empty` and collapses instead of leaving a blank frame.
					'flex rounded empty:hidden',
					showInput ? 'flex-col' : 'flex-wrap gap-xs px-2.5 py-3.5',
					theme.surface,
					theme.dashedStroke,
					className
				)}
			>
				{showInput ? (
					<div className="flex h-14 items-center px-2.5">
						<div className="relative flex min-w-0 flex-1">
							<span
								className={cn(
									'pointer-events-none absolute left-2 top-1/2 z-10 flex transition-[transform,opacity] duration-300',
									EASE,
									active
										? 'opacity-0 [transform:translate(-24px,-50%)]'
										: 'opacity-100 [transform:translate(0,-50%)]'
								)}
							>
								<OscarMark size={22} />
							</span>
							<Input
								placeholder={placeholder}
								value={question}
								onChange={handleChange}
								onFocus={handleFocus}
								onBlur={handleBlur}
								onPressEnter={handlePressEnter}
								className={cn(
									// White in every state, focused included.
									'!rounded-lg !border-grey-4 !bg-white transition-[padding] duration-300',
									EASE,
									active ? '!pl-3.5' : '!pl-[38px]'
								)}
							/>
						</div>
						<span
							className={cn(
								'flex shrink-0 overflow-hidden transition-[width,margin,opacity] duration-300',
								EASE,
								trimmed ? 'ml-xs w-[34px] opacity-100' : 'ml-0 w-0 opacity-0'
							)}
						>
							<OWButton
								type="primary"
								shape="circle"
								title="Send"
								tabIndex={trimmed ? 0 : -1}
								icon={<SendOutlined />}
								onMouseDown={keepFocus}
								onClick={send}
							/>
						</span>
					</div>
				) : null}
				{showInput ? (
					<div className="flex flex-wrap gap-xs px-2.5 pb-3.5 empty:hidden">{children}</div>
				) : (
					children
				)}
			</div>
		</OscarRoleProvider>
	);
};

export default withUserType(OscarAskInput);
